import {
	GRAPH_APPEARANCE_CONFIG_SCHEMA_VERSION,
	clampGraphAppearanceBrightness,
	normalizeGraphAppearanceHexColor,
	normalizePersistedGraphNodeTypeVisualConfig,
	presetGraphNodeTypeVisualConfig,
	type GraphAppearanceConfig,
	type PersistedGraphNodeTypeVisualConfig,
} from '../../../core/entities/graph-appearance-config';

export interface GraphNodeTypeVisualConfig {
	typeId: string;
	label: string;
	labelsVisible: boolean;
	colorHex: string;
	brightness: number;
}

interface GraphNodeTypeVisualPreset extends PersistedGraphNodeTypeVisualConfig {}

const MAX_GRAPH_NODE_TYPE_CONTROLS = 10;
const DEFAULT_GRAPH_NODE_TYPE_ORDER = ['Human', 'Project', 'Occupation'];
type GraphNodeTypeLabelKey = 'human' | 'project' | 'occupation';

export function presetForNodeType(typeId: string): GraphNodeTypeVisualPreset {
	return presetGraphNodeTypeVisualConfig(typeId);
}

export function collectGraphNodeTypes(nodes: Array<{ type: string }>): string[] {
	const uniqueTypes = new Set(DEFAULT_GRAPH_NODE_TYPE_ORDER);
	for (const node of nodes) {
		uniqueTypes.add(node.type);
	}

	return [...uniqueTypes].slice(0, MAX_GRAPH_NODE_TYPE_CONTROLS);
}

export function graphNodeTypeLabel(typeId: string, t: (key: GraphNodeTypeLabelKey) => string): string {
	if (typeId === 'Human') {
		return t('human');
	}

	if (typeId === 'Project') {
		return t('project');
	}

	if (typeId === 'Occupation') {
		return t('occupation');
	}

	return typeId;
}

export function synchronizeGraphNodeTypeVisualConfig(
	nodes: Array<{ type: string }>,
	currentConfig: Record<string, GraphNodeTypeVisualConfig>,
	t: (key: GraphNodeTypeLabelKey) => string,
): Record<string, GraphNodeTypeVisualConfig> {
	const nextConfig: Record<string, GraphNodeTypeVisualConfig> = {};
	for (const typeId of collectGraphNodeTypes(nodes)) {
		const preset = presetForNodeType(typeId);
		nextConfig[typeId] = {
			typeId,
			label: graphNodeTypeLabel(typeId, t),
			labelsVisible: currentConfig[typeId]?.labelsVisible ?? preset.labelsVisible,
			colorHex: currentConfig[typeId]?.colorHex ?? preset.colorHex,
			brightness: currentConfig[typeId]?.brightness ?? preset.brightness,
		};
	}

	return nextConfig;
}

export function graphNodeTypeVisualControls(
	nodes: Array<{ type: string }>,
	currentConfig: Record<string, GraphNodeTypeVisualConfig>,
): GraphNodeTypeVisualConfig[] {
	return collectGraphNodeTypes(nodes)
		.map((typeId) => currentConfig[typeId])
		.filter((config): config is GraphNodeTypeVisualConfig => Boolean(config));
}

export function updateGraphNodeTypeLabelsVisible(
	currentConfig: Record<string, GraphNodeTypeVisualConfig>,
	typeId: string,
): Record<string, GraphNodeTypeVisualConfig> {
	const current = currentConfig[typeId] ?? createFallbackGraphNodeTypeConfig(typeId);
	return {
		...currentConfig,
		[typeId]: {
			...current,
			labelsVisible: !current.labelsVisible,
		},
	};
}

export function updateGraphNodeTypeColor(
	currentConfig: Record<string, GraphNodeTypeVisualConfig>,
	typeId: string,
	colorHex: string,
): Record<string, GraphNodeTypeVisualConfig> {
	const current = currentConfig[typeId] ?? createFallbackGraphNodeTypeConfig(typeId);
	const preset = presetForNodeType(typeId);
	return {
		...currentConfig,
		[typeId]: {
			...current,
			colorHex: normalizeGraphAppearanceHexColor(colorHex, preset.colorHex),
		},
	};
}

export function updateGraphNodeTypeBrightness(
	currentConfig: Record<string, GraphNodeTypeVisualConfig>,
	typeId: string,
	brightness: number,
): Record<string, GraphNodeTypeVisualConfig> {
	const current = currentConfig[typeId] ?? createFallbackGraphNodeTypeConfig(typeId);
	return {
		...currentConfig,
		[typeId]: {
			...current,
			brightness: clampGraphAppearanceBrightness(brightness),
		},
	};
}

export function serializeGraphAppearanceConfig(
	currentConfig: Record<string, GraphNodeTypeVisualConfig>,
): GraphAppearanceConfig {
	return {
		schemaVersion: GRAPH_APPEARANCE_CONFIG_SCHEMA_VERSION,
		nodeTypeVisuals: Object.fromEntries(
			Object.entries(currentConfig).map(([typeId, config]) => [
				typeId,
				normalizePersistedGraphNodeTypeVisualConfig(typeId, config),
			]),
		),
	};
}

export function deserializeGraphAppearanceConfig(
	config: GraphAppearanceConfig,
): Record<string, GraphNodeTypeVisualConfig> {
	return Object.fromEntries(
		Object.entries(config.nodeTypeVisuals).map(([typeId, storedConfig]) => [
			typeId,
			{
				typeId,
				label: typeId,
				...normalizePersistedGraphNodeTypeVisualConfig(typeId, storedConfig),
			},
		]),
	);
}

export function toRgba(hexColor: string, alpha: number): string {
	const rgb = hexToRgb(hexColor);
	if (!rgb) {
		return `rgba(255, 255, 255, ${alpha})`;
	}

	return `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${alpha})`;
}

export function mixHexColors(startHex: string, endHex: string, ratio: number): string {
	const start = hexToRgb(startHex);
	const end = hexToRgb(endHex);
	if (!start || !end) {
		return endHex;
	}

	const mixChannel = (startChannel: number, endChannel: number) =>
		Math.round(startChannel + (endChannel - startChannel) * clamp(ratio, 0, 1));

	return rgbToHex(
		mixChannel(start.red, end.red),
		mixChannel(start.green, end.green),
		mixChannel(start.blue, end.blue),
	);
}

function createFallbackGraphNodeTypeConfig(typeId: string): GraphNodeTypeVisualConfig {
	const preset = presetForNodeType(typeId);
	return {
		typeId,
		label: typeId,
		labelsVisible: preset.labelsVisible,
		colorHex: preset.colorHex,
		brightness: preset.brightness,
	};
}

function hexToRgb(hexColor: string): { red: number; green: number; blue: number } | null {
	const normalized = hexColor.replace('#', '');
	const expanded = normalized.length === 3
		? normalized.split('').map((value) => `${value}${value}`).join('')
		: normalized;
	const red = Number.parseInt(expanded.slice(0, 2), 16);
	const green = Number.parseInt(expanded.slice(2, 4), 16);
	const blue = Number.parseInt(expanded.slice(4, 6), 16);

	if ([red, green, blue].some((value) => Number.isNaN(value))) {
		return null;
	}

	return { red, green, blue };
}

function rgbToHex(red: number, green: number, blue: number): string {
	return `#${[red, green, blue]
		.map((value) => clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0'))
		.join('')}`;
}

function clamp(value: number, minimum: number, maximum: number): number {
	return Math.min(maximum, Math.max(minimum, value));
}